import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Activity, Filter, Search, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import api from '../lib/api';

const ACTION_BADGE = {
  'source.create':    { color: '#2D5C3F', bg: '#E5EFE8', label: 'Source · Created' },
  'source.update':    { color: '#7A5617', bg: '#FBF6EA', label: 'Source · Updated' },
  'source.delete':    { color: '#9B2C2C', bg: '#F5E4E4', label: 'Source · Deleted' },
  'dropdown.add':     { color: '#2D5C3F', bg: '#E5EFE8', label: 'Dropdown · Added' },
  'dropdown.remove':  { color: '#9B2C2C', bg: '#F5E4E4', label: 'Dropdown · Removed' },
  'dropdown.rename':  { color: '#7A5617', bg: '#FBF6EA', label: 'Dropdown · Renamed' },
  'snapshot.create':  { color: '#2D5C3F', bg: '#E5EFE8', label: 'Reporting Date · Created' },
  'asset.create':     { color: '#2D5C3F', bg: '#E5EFE8', label: 'Asset · Created' },
  'asset.update':     { color: '#7A5617', bg: '#FBF6EA', label: 'Asset · Updated' },
  'asset.delete':     { color: '#9B2C2C', bg: '#F5E4E4', label: 'Asset · Deleted' },
  'holdings.save':    { color: '#1A3626', bg: '#E5EFE8', label: 'Holdings · Saved' },
  'lock.source':      { color: '#7A5617', bg: '#FBF6EA', label: 'Lock · Source' },
  'lock.period':      { color: '#7A5617', bg: '#FBF6EA', label: 'Lock · Period' },
  'backup.add':       { color: '#2D5C3F', bg: '#E5EFE8', label: 'Backup · Attached' },
  'backup.remove':    { color: '#9B2C2C', bg: '#F5E4E4', label: 'Backup · Removed' },
  'fx.set':           { color: '#1A3626', bg: '#E5EFE8', label: 'FX · Updated' },
  'view.create':      { color: '#2D5C3F', bg: '#E5EFE8', label: 'View · Created' },
  'view.update':      { color: '#7A5617', bg: '#FBF6EA', label: 'View · Updated' },
  'view.delete':      { color: '#9B2C2C', bg: '#F5E4E4', label: 'View · Deleted' },
  'excel.upload':     { color: '#1A3626', bg: '#E5EFE8', label: 'Excel · Uploaded' },
  'workspace.reset':  { color: '#9B2C2C', bg: '#F5E4E4', label: 'Workspace · Reset' },
  'workspace.migrate':{ color: '#1A3626', bg: '#E5EFE8', label: 'Workspace · Migrated' },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, byAction: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([api.listActivity(), api.activityStats()]);
      setRows(list || []);
      setStats(st || { total: 0, byAction: [] });
    } catch (e) {
      toast.error(e.message || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (!q) return true;
      return (r.summary || '').toLowerCase().includes(q) ||
             (r.action || '').toLowerCase().includes(q) ||
             (r.entity || '').toLowerCase().includes(q);
    });
  }, [rows, query, actionFilter]);

  return (
    <>
      <PageHeader
        overline="04 · Audit"
        title="Activity Log"
        description={`Every write to your workspace is timestamped and traceable. ${stats.total} entries recorded.`}
        actions={
          <button onClick={load} className="btn-secondary" data-testid="activity-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-4 gap-px bg-border border border-border mb-6" data-testid="activity-stats">
        <div className="bg-white p-5">
          <div className="overline mb-2">Total entries</div>
          <div className="font-heading text-[26px] font-bold tracking-tight tabular-nums">{stats.total}</div>
        </div>
        {(stats.byAction || []).slice(0, 3).map((s) => {
          const meta = ACTION_BADGE[s.action] || { label: s.action, color: '#575757', bg: '#F6F6F4' };
          return (
            <div key={s.action} className="bg-white p-5">
              <div className="overline mb-2">{meta.label}</div>
              <div className="font-heading text-[26px] font-bold tracking-tight tabular-nums">{s.count}</div>
            </div>
          );
        })}
        {/* Pad if fewer than 3 actions */}
        {Array.from({ length: Math.max(0, 3 - (stats.byAction || []).length) }).map((_, i) => (
          <div key={`pad-${i}`} className="bg-white p-5 opacity-50">
            <div className="overline mb-2">—</div>
            <div className="font-heading text-[26px] font-bold tracking-tight tabular-nums text-[#AAA]">0</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="border border-border bg-[#FAFAF7] p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="activity-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <Search size={13} className="text-[#787872]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search summary, action, entity…"
            className="flex-1 bg-transparent outline-none text-[12px] font-mono"
            data-testid="activity-search"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-[11px] font-mono text-[#787872] hover:text-[hsl(var(--destructive))]">
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-[#787872]" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="text-[12px] font-mono border border-border bg-white px-2 py-1 outline-none"
            data-testid="activity-action-filter"
          >
            <option value="">All actions</option>
            {(stats.byAction || []).map((s) => (
              <option key={s.action} value={s.action}>
                {(ACTION_BADGE[s.action]?.label || s.action)} · {s.count}
              </option>
            ))}
          </select>
          <span className="text-[11px] font-mono text-[#787872]">
            {filtered.length} / {rows.length}
          </span>
        </div>
      </div>

      {/* Activity table */}
      <div className="border border-border bg-white">
        <table className="grid-table" data-testid="activity-table">
          <thead>
            <tr>
              <th className="min-w-[180px]">When</th>
              <th className="min-w-[180px]">Action</th>
              <th>Summary</th>
              <th className="num">Meta</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="text-center py-6 text-[#AAA] text-xs">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-[#AAA] text-xs">
                <Activity size={20} className="inline-block mr-2 opacity-40" />
                No activity recorded yet. Make any change to the workspace and it'll appear here.
              </td></tr>
            )}
            {filtered.map((r) => {
              const meta = ACTION_BADGE[r.action] || { label: r.action, color: '#575757', bg: '#F6F6F4' };
              return (
                <tr key={r.id} data-testid={`activity-row-${r.id}`}>
                  <td>
                    <div className="font-mono text-[12px] tabular-nums">{fmtTime(r.at)}</div>
                    <div className="text-[10px] text-[#787872] font-mono">{relTime(r.at)}</div>
                  </td>
                  <td>
                    <span
                      className="inline-flex items-center font-heading font-semibold text-[11px] px-2 py-0.5 rounded"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </td>
                  <td className="text-[13px]">{r.summary}</td>
                  <td className="num">
                    {r.meta && Object.keys(r.meta).length > 0 ? (
                      <code className="text-[10px] font-mono text-[#575757] bg-[#F6F6F4] px-1.5 py-0.5">
                        {Object.entries(r.meta)
                          .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                          .join(' · ')}
                      </code>
                    ) : (
                      <span className="text-[#CCC]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
